/**
 * Browser side of platform tags (lib/platform-block.ts, server-only). Content fetched in the browser never passes the search route,
 * so it is checked by /api/booru/screen, which answers only the keys to drop. If the check cannot be reached, nothing from the batch is
 * shown (fail closed): showing unchecked content is worse than an empty result.
 */
interface ScreenablePost {
  id: string | number
  site?: string | null
  tags?: readonly string[] | string | null
  title?: string | null
}

const keyOf = (post: ScreenablePost, index: number) => `${post.site ?? ""}:${post.id}:${index}`

/** The screen route checks at most 400 items per call; batches stay under that so nothing is left unchecked. */
const BATCH = 300

export async function screenPosts<T extends ScreenablePost>(posts: readonly T[]): Promise<T[]> {
  if (posts.length === 0) return []
  if (posts.length > BATCH) {
    const out: T[] = []
    for (let i = 0; i < posts.length; i += BATCH) out.push(...(await screenBatch(posts.slice(i, i + BATCH))))
    return out
  }
  return screenBatch(posts)
}

async function screenBatch<T extends ScreenablePost>(posts: readonly T[]): Promise<T[]> {
  try {
    const response = await fetch("/api/booru/screen", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: posts.map((post, i) => ({ key: keyOf(post, i), tags: post.tags ?? [], title: post.title ?? undefined })) }),
    })
    if (!response.ok) return []
    const { blocked } = (await response.json()) as { blocked?: string[] }
    const drop = new Set(Array.isArray(blocked) ? blocked : [])
    return posts.filter((post, i) => !drop.has(keyOf(post, i)))
  } catch {
    return []
  }
}

/** True when the platform check says this one item may be shown. Fails closed. */
export async function mayShow(item: ScreenablePost): Promise<boolean> {
  return (await screenPosts([item])).length === 1
}
